/**
 * The ark protocol's parameter VOCABULARY, and the parse → collapse → overwrite
 * that turns one tenant request into ONE canonical request value (#1883, owner
 * verdict 2026-08-16; predecessors #1897/#1900, parent #1838).
 *
 * ── THE LAW OF THIS MODULE, AND THE LAW IT REPLACES ───────────────────────
 *
 * Current law, in three sentences:
 *
 *   1. **The vocabulary is protocol code.** Which parameter names exist, what
 *      type each carries, and which prompt-command spellings state it are
 *      facts about the ark task PROTOCOL — a stable superset — and they live
 *      here, versioned with the protocol. A vendor generation that adds a
 *      parameter (sd3.0, say) is a protocol v2: a second vocabulary constant
 *      beside this one, a deliberate diff, never operator data.
 *   2. **Value domains are NOT code, and they are not operator data either —
 *      the upstream owns them.** This module refuses only obvious garbage
 *      (a non-integer, a negative count, an empty string, an absurd length);
 *      whether 31 seconds or "5k" is servable is the upstream's own refusal
 *      to make, synchronously at submit, where #1874 §6 measured it making
 *      exactly such refusals.
 *   3. **Billing-legal values are the PRICE MATRIX.** A parameter that names a
 *      price tier (`resolution`) is admitted here on type alone; whether the
 *      order can be BILLED is ADR-012 §3's exact-match gate — no coefficient
 *      entry or no `(unit, variant_key)` price row means a 400 before any
 *      upstream spend. The money gate was never this module's and still is
 *      not; what changed is that nothing else pretends to be one.
 *
 * ── THE OVERTHROWN LAW, RECORDED SO ITS REVERSAL IS AUDITABLE ─────────────
 *
 * This file used to open with the opposite rule: "no admitted name, no
 * admitted value, no bound and no default is written down in this module" —
 * the parameter surface was an OPERATOR DOCUMENT (`task_parameter_surface`),
 * validated at the ops write face, published on the pub projection, and this
 * module was a total function over it. The argument was #1838's growth law:
 * two models of one family differ only in a bound (15s vs 30s), so a branch
 * per model is an N×M explosion, therefore names-as-data. A mutation suite
 * enforced it by renaming every parameter and requiring identical behaviour.
 *
 * The owner's 2026-08-16 verdict overturned it, and the flaw in the old
 * argument is worth stating precisely: it conflated three axes that have
 * three different owners. The AXIS OF NAMES is protocol-stable — seedance
 * 2.0 and 2.5 differ in a BOUND, never in a name — so names-as-data bought
 * nothing there. The AXIS OF DOMAINS (the bound that does differ) belongs to
 * the upstream, which enforces it authoritatively at submit; transcribing it
 * into an operator document produced a second, staler copy that could only
 * ever refuse requests the upstream would have served or admit requests it
 * refuses. And the AXIS OF MONEY was already the price matrix's. What the
 * N×M argument actually justified was domains-as-data, and domains turn out
 * to need no home at all. The per-SKU cost of the old law was real: every
 * listing transcribed a vendor's value tables by hand, and a typo was a
 * silently mis-clamped paid order. Under the new law a new SKU of an
 * existing media type costs spec + price rows + supply pin — the parameter
 * surface line item is deleted, not moved.
 *
 * ── THE TWO FORMS, AND WHY BOTH ARE STILL RECOGNISED ──────────────────────
 *
 * The vendor's surface states a parameter twice over: as a field in
 * `parameters`, and as a text command inside the prompt (`--dur 5`). Both are
 * recognised, the STRUCTURED field wins where both spoke, and recognised
 * commands are REMOVED from the prompt with the canonical value written into
 * the structured field — so the dispatched request states each parameter
 * exactly once and the upstream's (unmeasured) precedence between the two
 * forms is moot. The command spellings, long and short, come from the
 * vendor's own documentation; being wider than the vendor only costs a
 * refusal a tenant can act on, while being narrower leaks an unread command
 * into the prompt as prose — the same asymmetry the old module argued, and it
 * survives the law change intact. Within a prompt a repeated command is not
 * an error: the LAST occurrence wins, the way a shell treats a repeated flag.
 *
 * A prompt command outside the vocabulary (`--foo`) is prose and is left
 * alone. An unknown STRUCTURED name is refused: it would otherwise reach the
 * upstream verbatim, and "the protocol is a stable superset" means exactly
 * that the set of names is closed here — a name outside it is not a value
 * question for the upstream, it is a request this protocol version cannot
 * state. A garbage value on a DECLARED command is refused even when a
 * structured field superseded it: the tenant wrote an instruction we could
 * not read, and dropping it silently would serve them a generation that
 * ignored it.
 *
 * ── WHAT IS DELIBERATELY ABSENT ───────────────────────────────────────────
 *
 * No defaults: a parameter neither form stated stays ABSENT and the upstream
 * applies its own default, which is the domain owner defaulting its own
 * domain. No bounds beyond the garbage caps. No enum value lists. No
 * `metered_quantity_parameter`: the unit lane's billable quantity now comes
 * from the upstream's TERMINAL REPORT (ADR-012 §2 as amended by #1883), so
 * no field of the order names the amount and the binding died with the
 * surface document.
 */

import type { ArkTaskContentPart } from './ark-tasks.ts';

/** A whole-number parameter (a count the order states, e.g. seconds). */
export interface ArkTaskIntegerParameter {
  readonly kind: 'integer';
  readonly name: string;
  /** Prompt command spellings, e.g. `--duration`. Lowercase `--word` only. */
  readonly commands: readonly string[];
  /**
   * Protocol-legal values the request-shape cap would otherwise refuse.
   *
   * `-1` on `duration` is Seedance's documented "smart duration" sentinel
   * for `edit` / `extend` (measured 2026-08-30: those two task types require
   * it; `reference` does not). This is NOT a domain bound and it is NOT a
   * billing opinion: it is a named exception to the garbage cap on a
   * negative count. A SKU that sells duration as a billed UNIT still has to
   * refuse `-1` at the route gate — this module does not know about lanes.
   */
  readonly sentinels?: readonly number[];
}

/** A free-spelling parameter whose value domain the upstream owns. */
export interface ArkTaskStringParameter {
  readonly kind: 'string';
  readonly name: string;
  readonly commands: readonly string[];
}

/**
 * A two-valued parameter whose JSON form is a boolean.
 *
 * Added with V2: `generate_audio` and `return_last_frame` are booleans on
 * the measured JoJoKey / Seedance wire. Carrying them as `string` `"true"`
 * or as `integer` `1` would emit a different JSON type than the upstream
 * accepts. The kind is the type; the two values ARE the type, not a domain
 * list this module has no business enumerating.
 *
 * Prompt form admits the tokens `true` / `false` (any ASCII case) and
 * normalises to a JSON boolean BEFORE dispatch, the same widening
 * `readInteger` already does for `"5"` → `5`. JSON booleans pass through.
 * Integers `0`/`1` are refused: that mapping is not on the wire.
 */
export interface ArkTaskBooleanParameter {
  readonly kind: 'boolean';
  readonly name: string;
  readonly commands: readonly string[];
}

export type ArkTaskProtocolParameter =
  | ArkTaskIntegerParameter
  | ArkTaskStringParameter
  | ArkTaskBooleanParameter;

/** One admitted parameter value, after both forms are collapsed. */
export type ArkTaskParameterValue = string | number | boolean;

/** Vocabulary names only, in declaration order. */
export type ArkTaskParameterMap = Readonly<Record<string, ArkTaskParameterValue>>;

/**
 * The closed set of vocabulary versions a SKU may sell.
 *
 * Selecting one is SKU data (`task_admission.parameter_vocabulary`); the
 * arrays themselves stay protocol code. Omitted on a stored document means
 * `v1` — every SKU listed before this field existed, and every SKU not yet
 * cut over. Adding a member here is a protocol version, same as adding a
 * parameter inside an array.
 */
export const ARK_TASK_PARAMETER_VOCABULARY_IDS = ['v1', 'v2'] as const;
export type ArkTaskParameterVocabularyId = (typeof ARK_TASK_PARAMETER_VOCABULARY_IDS)[number];

export function isArkTaskParameterVocabularyId(
  value: unknown,
): value is ArkTaskParameterVocabularyId {
  return value === 'v1' || value === 'v2';
}

/**
 * The ark task protocol's parameter vocabulary, version 1.
 *
 * Declaration order is the emission order of the canonical `parameters`
 * object, so the dispatched body is byte-stable for a given input — which is
 * what makes the request fingerprint, a trace diff and a fixture comparison
 * mean anything.
 *
 * The command spellings are the vendor's documented long and short forms.
 * Recognising a spelling the vendor ignores costs a tenant an error message;
 * missing one the vendor honours leaks an uncollapsed value into the prompt —
 * so the set errs wide, exactly as the module header argues.
 *
 * ADDING A MEMBER IS A PROTOCOL VERSION, not an edit: a vendor surface that
 * grows a parameter is a new sales-form generation, and the next vocabulary
 * lands as `ARK_TASK_PARAMETERS_V2` beside this one with its own review —
 * never as a quiet push into this array, which every stored fingerprint and
 * every declared admission document reads through.
 */
export const ARK_TASK_PARAMETERS_V1: readonly ArkTaskProtocolParameter[] = [
  { kind: 'integer', name: 'duration', commands: ['--duration', '--dur'] },
  { kind: 'string', name: 'resolution', commands: ['--resolution', '--rs'] },
  { kind: 'string', name: 'ratio', commands: ['--ratio', '--rt'] },
] as const;

/**
 * The ark task protocol's parameter vocabulary, version 2.
 *
 * Seedance 2.5's omni-reference generation (measured 2026-08-30 against
 * JoJoKey CN, paid): video reference, audio reference, `edit`, `extend`.
 * V1's three members stay in declaration order at the front — growing the
 * array by APPEND only — so a V1-shaped request still emits the same
 * three-key object, byte-identical, and a stored fingerprint does not
 * move. New members:
 *
 *   - `omni_reference_task_type` (`auto` / `reference` / `edit` / `extend`).
 *     `reference` + `duration: 4` is video reference; `edit`/`extend`
 *     require `duration: -1`. Omitting the field lets the upstream default
 *     to `auto`, which is what mis-classified a reference job as edit.
 *   - `generate_audio`, `return_last_frame` — JSON booleans on the wire.
 *   - `seed` — integer. **2.0 supports it; 2.5 returns `unsupported_seed`.**
 *     That is a SKU / upstream-domain difference, not a protocol one: V2
 *     admits the name so a 2.0 SKU can state it, and a 2.5 SKU that sends
 *     it is answered by the upstream. Do not encode the 2.5 refusal here.
 *
 * `duration` on V2 declares sentinel `-1`. The garbage cap on a negative
 * count stays; this is the one documented exception. A unit/seconds-lane
 * SKU must still refuse `-1` at the route gate (duration is that lane's
 * billed quantity; a negative would compute a negative charge). Token-lane
 * SKUs (`ark_video_token_v1`, `ark_video_token_tiered_v1`) do not price
 * duration, so `-1` is harmless there.
 *
 * Which SKU sells V2 is data: `task_admission.parameter_vocabulary = "v2"`.
 * This constant is the vocabulary; it is not a default. {@link ARK_TASK_PARAMETERS}
 * stays aliased to V1 so every unmigrated SKU, every stored fingerprint,
 * and every caller that does not pass a vocabulary keep V1 behaviour.
 */
export const ARK_TASK_PARAMETERS_V2: readonly ArkTaskProtocolParameter[] = [
  { kind: 'integer', name: 'duration', commands: ['--duration', '--dur'], sentinels: [-1] },
  { kind: 'string', name: 'resolution', commands: ['--resolution', '--rs'] },
  { kind: 'string', name: 'ratio', commands: ['--ratio', '--rt'] },
  { kind: 'string', name: 'omni_reference_task_type', commands: ['--omni_reference_task_type'] },
  { kind: 'boolean', name: 'generate_audio', commands: ['--generate_audio'] },
  { kind: 'boolean', name: 'return_last_frame', commands: ['--return_last_frame'] },
  { kind: 'integer', name: 'seed', commands: ['--seed'] },
];

/**
 * The vocabulary the current protocol version admits.
 *
 * STAYS V1. Switching this alias to V2 would silently reopen every
 * unmigrated SKU's closed name set and change every stored fingerprint
 * that hashed a V1 canonical object. SKUs opt into V2 through
 * `task_admission.parameter_vocabulary`; callers that need a version pass
 * it to {@link resolveArkTaskParameters}.
 */
export const ARK_TASK_PARAMETERS: readonly ArkTaskProtocolParameter[] = ARK_TASK_PARAMETERS_V1;

/** The vocabulary array for a version id. Unknown ids do not compile. */
export function arkTaskParametersForVocabulary(
  id: ArkTaskParameterVocabularyId,
): readonly ArkTaskProtocolParameter[] {
  if (id === 'v2') return ARK_TASK_PARAMETERS_V2;
  return ARK_TASK_PARAMETERS_V1;
}

/** The declared parameter of that name, or `null`. Exact spelling only. */
export function arkTaskParameterNamed(
  name: string,
  vocabulary: readonly ArkTaskProtocolParameter[] = ARK_TASK_PARAMETERS,
): ArkTaskProtocolParameter | null {
  return vocabulary.find((candidate) => candidate.name === name) ?? null;
}

/**
 * The garbage cap on an integer value — a bound on the REQUEST, not a domain
 * fact. A real domain sits orders of magnitude below it; a value above it is
 * a typo or a probe, and refusing locally costs nothing where dispatching it
 * buys an upstream 400 at our latency.
 */
const MAX_INTEGER_VALUE = 1_000_000;

/**
 * The garbage shape of a string value. Deliberately wider than an identifier
 * — real vendor values carry `:` and `.` (`16:9`, `480P`) — and deliberately
 * not free text: the value is echoed into the dispatched body and into
 * refusal messages, so control characters, whitespace and absurd lengths are
 * refused as garbage. Which SPELLINGS inside this alphabet are servable is
 * the upstream's own question.
 */
const STRING_VALUE = /^[A-Za-z0-9:._-]{1,64}$/;

/** Separates a command from its value in the joined `--flag=value` spelling. */
const COMMAND_VALUE_SEPARATOR = '=';

export type ArkTaskParameterRefusalReason =
  /** A structured name outside the protocol vocabulary. */
  | 'unknown_parameter'
  /** An integer parameter carried something that is not a whole number. */
  | 'not_an_integer'
  /** A whole number, but negative or absurdly large — garbage, not a domain verdict. */
  | 'out_of_range'
  /** A string parameter carried garbage: empty, absurdly long, or unspeakable. */
  | 'invalid_value'
  /** A declared command with no readable value beside it. */
  | 'command_without_value';

export interface ArkTaskParameterRefusal {
  readonly reason: ArkTaskParameterRefusalReason;
  /** The parameter the tenant must change. */
  readonly parameter: string;
  /** Tenant-facing, and built from the vocabulary — never from a value echo. */
  readonly message: string;
}

/** The one canonical statement of a request, after both forms are collapsed. */
export interface ArkTaskCanonicalRequest {
  /**
   * The content parts with every RECOGNISED command removed. A part in which
   * nothing was recognised is returned byte-identical — this module rewrites
   * a tenant's prompt only where it took something out of it.
   *
   * {@link ArkTaskContentPart} is open (see its own header): every key the
   * tenant sent survives, and the type says so, so a caller rebuilding a
   * request body from this cannot be told the only readable fields are `type`
   * and `text` and silently drop the rest.
   *
   * SEAM — a part whose whole text WAS the command comes back with `text: ''`,
   * and this module deliberately does not decide what happens next: how the
   * upstream answers an empty text part is unmeasured, and the dispatching
   * caller is where a rule belongs once someone has measured it.
   */
  readonly content: readonly ArkTaskContentPart[];
  /** Vocabulary names only, in declaration order, values as the tenant spelt them. */
  readonly parameters: ArkTaskParameterMap;
}

export type ArkTaskParameterResolution =
  | { readonly ok: true; readonly value: ArkTaskCanonicalRequest }
  | { readonly ok: false; readonly refusal: ArkTaskParameterRefusal };

export interface ArkTaskParameterInput {
  readonly content: readonly ArkTaskContentPart[];
  readonly parameters?: Readonly<Record<string, unknown>> | undefined;
}

export interface ArkTaskParameterResolveOptions {
  /**
   * The vocabulary this request is resolved against. Defaults to
   * {@link ARK_TASK_PARAMETERS} (V1). The route gate passes V2 when the
   * SKU's admission document says so; callers that omit this keep V1
   * behaviour byte-identical, which is the unmigrated-SKU contract.
   */
  readonly vocabulary?: readonly ArkTaskProtocolParameter[];
}

/**
 * Collapse both forms into one canonical request, or refuse.
 *
 * Total over the supplied vocabulary (default {@link ARK_TASK_PARAMETERS},
 * which is V1) and nothing else: no SKU document, no operator data, no
 * default. Which vocabulary a SKU sells is the CALLER's question — the
 * route gate reads `task_admission.parameter_vocabulary` and passes the
 * matching array. What this function checks is TYPE and garbage; what it
 * does not check — domains — is the upstream's, and what makes the result
 * billable — the price matrix, and the unit-lane ban on a negative
 * duration — is the route gate's.
 */
export function resolveArkTaskParameters(
  input: ArkTaskParameterInput,
  options?: ArkTaskParameterResolveOptions,
): ArkTaskParameterResolution {
  const vocabulary = options?.vocabulary ?? ARK_TASK_PARAMETERS;
  const byCommand = new Map<string, ArkTaskProtocolParameter>();
  for (const rule of vocabulary) {
    for (const command of rule.commands) byCommand.set(command, rule);
  }

  // Pass one: the prompt. Weaker source, so it is gathered first and then
  // overwritten by the structured form below.
  const resolved = new Map<string, ArkTaskParameterValue>();
  const content: ArkTaskContentPart[] = [];
  for (const part of input.content) {
    if (typeof part.text !== 'string') {
      // Shallow-spread: every key the tenant sent survives, including
      // `video_url` / `audio_url` / `role`. This is the V2 omni-reference
      // content path — audio reference is a `content[]` `audio_url` part,
      // not the documented-unavailable `audios` field (measured 2026-08-30).
      //
      // Do not assume only `image_url` parts can draw an upstream
      // real-person refusal. The one measured refusal named `input image
      // 'content[0]'`; a 2026-08-30 video-reference job whose clip contained
      // identifiable AIGC people (plain HTTPS URL, JoJoKey CN) submitted 200
      // and succeeded (¥3.5799 / 99111 tokens / with_media) without that
      // refusal. That is n=1, the clip was a TOS object under the same
      // vendor account (possible allowlist), and the people were in motion —
      // it does not prove video is never refused. If a video or audio part
      // later is, recovery would need to extend past image-only
      // `asset_type: 1` to types 2 (video) and 3 (audio); the register API
      // already accepts those (measured: type 2 registered and used for
      // `edit`/`extend`). This PR does not add that walk.
      content.push({ ...part });
      continue;
    }
    const stripped = stripCommands(part.text, byCommand, resolved);
    if (!stripped.ok) return stripped;
    content.push(stripped.text === part.text ? { ...part } : { ...part, text: stripped.text });
  }

  // Pass two: the structured form wins wherever both spoke.
  for (const [name, raw] of Object.entries(input.parameters ?? {})) {
    const rule = arkTaskParameterNamed(name, vocabulary);
    if (rule === null) {
      return refuse('unknown_parameter', name, `${name} is not a supported parameter for this model.`);
    }
    const admitted = admit(rule, raw);
    if (!admitted.ok) return admitted;
    resolved.set(name, admitted.value);
  }

  // Pass three: emit in vocabulary order. A parameter neither form stated
  // stays ABSENT — there are no defaults here; the upstream defaults its own
  // domain, which is the domain owner's call to make.
  const parameters: Record<string, ArkTaskParameterValue> = {};
  for (const rule of vocabulary) {
    const value = resolved.get(rule.name);
    if (value !== undefined) parameters[rule.name] = value;
  }
  return { ok: true, value: { content, parameters } };
}

interface StripSuccess {
  readonly ok: true;
  readonly text: string;
}

function stripCommands(
  text: string,
  byCommand: ReadonlyMap<string, ArkTaskProtocolParameter>,
  resolved: Map<string, ArkTaskParameterValue>,
): StripSuccess | { readonly ok: false; readonly refusal: ArkTaskParameterRefusal } {
  const tokens: { value: string; start: number; end: number }[] = [];
  const pattern = /\S+/g;
  for (let match = pattern.exec(text); match !== null; match = pattern.exec(text)) {
    tokens.push({ value: match[0], start: match.index, end: match.index + match[0].length });
  }
  const cuts: { start: number; end: number }[] = [];
  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];
    if (token === undefined) continue;
    // Both spellings, against ONE declaration, and case-folded: `--flag value`
    // is two tokens; `--flag=value` is one, split at the first separator so a
    // value may contain further ones.
    const folded = token.value.toLowerCase();
    const separator = folded.indexOf(COMMAND_VALUE_SEPARATOR);
    const joined = separator > 0 ? byCommand.get(folded.slice(0, separator)) : undefined;
    const rule = byCommand.get(folded) ?? joined;
    // Not a declared command in any spelling: prose. Left exactly where the
    // tenant wrote it.
    if (rule === undefined) continue;
    const next = joined === undefined ? tokens[index + 1] : undefined;
    // A command and its value must be on ONE line.
    //
    // The tokenizer does not see line breaks, so without this a `--flag` ending
    // a line would pair with the first word of the next one and the cut would
    // swallow the newline between them — silently reflowing the prompt, which
    // is exactly what the removal rule below promises never to happen. Refusing
    // is the fail-closed half of that promise: the tenant is told, before
    // anything is dialled, rather than being served a prompt we reshaped.
    const spansLines = next !== undefined && text.slice(token.end, next.start).includes('\n');
    const argument = joined === undefined
      ? (spansLines ? undefined : next?.value)
      : token.value.slice(separator + COMMAND_VALUE_SEPARATOR.length);
    // `--flag` at the end of the text, `--flag` at the end of a line, and
    // `--flag=` with nothing after it are the same mistake and get the same
    // answer. None may fall through to prose: the flag is DECLARED by this
    // protocol, so its appearance is a statement, and a statement we cannot
    // read is not a statement we may drop.
    if (argument === undefined || argument.length === 0) {
      return refuse(
        'command_without_value',
        rule.name,
        `${token.value} must be followed by a value on the same line.`,
      );
    }
    const admitted = admit(rule, argument);
    if (!admitted.ok) return admitted;
    // Last statement wins, the way a shell treats a repeated flag.
    resolved.set(rule.name, admitted.value);
    if (next === undefined) {
      cuts.push({ start: token.start, end: token.end });
    } else {
      cuts.push({ start: token.start, end: next.end });
      index += 1;
    }
  }
  if (cuts.length === 0) return { ok: true, text };
  let out = '';
  let cursor = 0;
  for (const cut of cuts) {
    // Each removal takes ONE adjacent horizontal whitespace run with it — the
    // one before it, or the one after it when the command opened the text.
    // That is the whole rule: nothing else in the prompt is touched, so
    // newlines, indentation and the tenant's own double spaces all survive
    // exactly where they were. A global collapse-and-trim would have read as
    // this and quietly reflowed prose the tenant wrote on purpose.
    //
    // No cut can CONTAIN a newline either: a command that spans a line break
    // is refused above rather than paired across it, so neither arm here can
    // remove one. That is what makes "newlines are prompt structure" a fact
    // about this function rather than a hope about its inputs.
    const start = extendBack(text, cut.start, cursor);
    const end = start === cut.start ? extendForward(text, cut.end) : cut.end;
    out += text.slice(cursor, start);
    cursor = end;
  }
  out += text.slice(cursor);
  return { ok: true, text: out };
}

/** Back over one horizontal whitespace run, not past `floor`. */
function extendBack(text: string, from: number, floor: number): number {
  let index = from;
  while (index > floor && HORIZONTAL_SPACE.test(text.charAt(index - 1))) index -= 1;
  return index;
}

/** Forward over one horizontal whitespace run. */
function extendForward(text: string, from: number): number {
  let index = from;
  while (index < text.length && HORIZONTAL_SPACE.test(text.charAt(index))) index += 1;
  return index;
}

/** Whitespace that is not a line break. Newlines are prompt structure. */
const HORIZONTAL_SPACE = /[^\S\n]/;

/**
 * Type-and-garbage admission of one value, from either form.
 *
 * WHAT THIS NO LONGER DOES, stated because its absence is the point: no
 * min/max domain check, no enum membership, no case-normalisation to a
 * declared spelling. A tenant's `4k` and `4K` both dispatch as written and
 * the upstream — the domain's owner — answers for them; the price matrix
 * folds case where money needs one key (`variant-values` lowercases before
 * a `variant_key` is spelt), and the admission map matches tier classes
 * case-insensitively for the same reason.
 *
 * THE `value < 0` INTEGER GUARD is a request-shape cap, not a domain check.
 * A negative count is garbage the way a 1_000_001-second duration is
 * garbage: it is not a value the protocol can state as a whole-number
 * parameter, and refusing it locally costs nothing where dispatching it
 * would. It has been here since #1883. It is NOT "duration must be 4–15",
 * which is the upstream's. V2's `duration` sentinel `-1` is the one
 * documented exception (Seedance `edit`/`extend` smart duration, measured
 * 2026-08-30). A unit/seconds-lane SKU still cannot carry that sentinel —
 * duration is that lane's billed quantity, and a negative would compute a
 * negative charge — but that refusal lives at the route gate, which knows
 * the metering profile; this function does not.
 */
function admit(
  rule: ArkTaskProtocolParameter,
  raw: unknown,
): { readonly ok: true; readonly value: ArkTaskParameterValue }
  | { readonly ok: false; readonly refusal: ArkTaskParameterRefusal } {
  if (rule.kind === 'integer') {
    const value = readInteger(raw) ??
      (rule.sentinels !== undefined && rule.sentinels.length > 0
        ? readSignedIntegerToken(raw)
        : null);
    if (value === null) {
      return refuse('not_an_integer', rule.name, `${rule.name} must be a whole number.`);
    }
    if (rule.sentinels !== undefined && rule.sentinels.includes(value)) {
      return { ok: true, value };
    }
    if (value < 0 || value > MAX_INTEGER_VALUE) {
      return refuse(
        'out_of_range',
        rule.name,
        `${rule.name} must be a non-negative whole number below ${String(MAX_INTEGER_VALUE)}.`,
      );
    }
    return { ok: true, value };
  }
  if (rule.kind === 'boolean') {
    const value = readBoolean(raw);
    if (value === null) {
      return refuse('invalid_value', rule.name, `${rule.name} must be true or false.`);
    }
    return { ok: true, value };
  }
  if (typeof raw !== 'string') {
    return refuse('invalid_value', rule.name, `${rule.name} must be a string value.`);
  }
  const trimmed = raw.trim();
  if (!STRING_VALUE.test(trimmed)) {
    return refuse(
      'invalid_value',
      rule.name,
      `${rule.name} must be 1-64 characters of letters, digits, or ':', '.', '_', '-'.`,
    );
  }
  return { ok: true, value: trimmed };
}

/**
 * A whole number, from either form. The structured form carries a JSON number
 * and the prompt form carries a token, so both are admitted and normalised to
 * a number — one rule, rather than one branch per source.
 *
 * ── THIS IS WIDER THAN THE VENDOR'S SHAPE, ON PURPOSE ─────────────────────
 *
 * The captured request body carries `duration` as a JSON NUMBER, so accepting
 * the STRING `"5"` in the structured field admits something the vendor's own
 * surface probably would not. Kept, because every accepted string is
 * normalised to a number BEFORE dispatch — the widening is in what a tenant
 * may write, never in what we send or bill — and it fails safe: a tenant
 * whose SDK stringifies gets served instead of a refusal they cannot act on.
 * Pinned by test rather than left to drift.
 */
function readInteger(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isSafeInteger(raw) ? raw : null;
  if (typeof raw !== 'string') return null;
  return /^\d{1,9}$/.test(raw.trim()) ? Number(raw.trim()) : null;
}

/**
 * A signed whole-number TOKEN, used only when the rule declares sentinels.
 *
 * `readInteger` stays digits-only so a V1 `duration: "-1"` is still
 * `not_an_integer`, not `out_of_range` — that string form was never a
 * JSON number and V1 behaviour is pinned. V2 duration's sentinel needs
 * the prompt spelling `--dur -1` and the stringified structured form to
 * parse, which is this helper, and only for rules that declared one.
 */
function readSignedIntegerToken(raw: unknown): number | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!/^-?\d{1,9}$/.test(trimmed)) return null;
  return Number(trimmed);
}

function readBoolean(raw: unknown): boolean | null {
  if (raw === true || raw === false) return raw;
  if (typeof raw !== 'string') return null;
  const folded = raw.trim().toLowerCase();
  if (folded === 'true') return true;
  if (folded === 'false') return false;
  return null;
}

function refuse(
  reason: ArkTaskParameterRefusalReason,
  parameter: string,
  message: string,
): { readonly ok: false; readonly refusal: ArkTaskParameterRefusal } {
  return { ok: false, refusal: { reason, parameter, message } };
}
